import { useCustomForm } from "@/hooks/useCustomForm"
import { zodResolver } from "@hookform/resolvers/zod"
import {
  Modal,
  Box,
  Typography,
  TextField,
  Button,
  FormControl,
  IconButton,
} from "@mui/material"
import ClearIcon from "@mui/icons-material/Clear"
import { z } from "zod"
import SingleSelect from "@/components/Select/SingleSelect"
import { toast } from "sonner"
import { useQueryClient } from "@tanstack/react-query"
import React, { useCallback, useEffect, useState } from "react"
import { useOrderCrudContext } from "@/context/orderCrudContext"
import LetterAvatar from "@/components/Avatar/LetterAvatar"
import AddIcon from "@mui/icons-material/Add"
import Grid from "@mui/material/Grid2"
import Productable from "@/components/Products/Productable"
import SecondaryButton from "@/components/Buttons/SecondaryButton"
import PrimaryButton from "@/components/Buttons/PrimaryButton"
import { DevTool } from "@/components/FormDevTool"
import { useNavigate } from "react-router-dom"
import useSuppliers from "@/hooks/useSuppliers"
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs"
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider"
import { DatePicker } from "@mui/x-date-pickers/DatePicker"
import { useNow } from "@mui/x-date-pickers/internals"
import dayjs from "dayjs"
import PurchaseProductable from "@/components/Products/PurchaseProducatable"
import { Controller } from "react-hook-form"
import AddSupplierModal from "../suppliers/Model"
import { useAppSelector } from "@/lib/hooks"

const PurchaseSchema = z.object({
  supplier_id: z.number().transform((value) => Number(value)),
  reference_number: z.string(),
  date: z
    .string()
    .refine(
      (value) => dayjs(value, "YYYY-MM-DD", true).isValid(), // Ensure the date is valid and in the correct format
      { message: "Invalid date format. Use YYYY-MM-DD." }
    )
    .transform((value) => (value ? dayjs(value).endOf("day").toDate() : null)),
  products: z
    .array(
      z.object({
        /* your ProductableSchema here */
      })
    )
    .min(1, "At least one item must be included"),
})

const AddPurchaseModal = ({ open, onClose }) => {
  const canCreateSupplier = useAppSelector((state) =>
    state.auth.user?.permissions?.includes("business-suppliers-create")
  )
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  // const [searchInput, setSearchInput] = useState("")
  const {
    openOrderDialog,
    setOpenOrderDialog,
    selectedOrderRow,
    setSelectedOrderRow,
  } = useOrderCrudContext()

  const {
    api,
    register,
    handleSubmit,
    errors,
    isSubmitting,
    reset,
    setValue,
    control,
    Controller,
    getValues,
  } = useCustomForm({
    defaultValues: {
      date: dayjs().format("YYYY-MM-DD"),
    },
    resolver: zodResolver(PurchaseSchema),
  })

  const defaultOnClose = () => {
    setOpenOrderDialog(false)
    setSelectedOrderRow(null)
  }
  const { suppliers, isLoading: supplierLoading } = useSuppliers()

  useEffect(() => {
    if (suppliers.length > 0) {
      setValue("supplier_id", suppliers[0].id)
    }
  }, [suppliers])

  useEffect(() => {
    if (selectedOrderRow) {
      Object.entries(selectedOrderRow).forEach(([key, value]) => {
        if (typeof value === "object" && value !== null) {
          Object.entries(value).forEach(([nestedKey, nestedValue]) => {
            setValue(`${key}.${nestedKey}`, nestedValue)
          })
        } else {
          setValue(`${key}`, value)
        }
      })
    } else {
      reset()
    }
  }, [selectedOrderRow, setValue, reset])

  const onSubmit = async (data) => {
    const method = selectedOrderRow ? "PUT" : "POST"
    const url = selectedOrderRow
      ? `purchase/${selectedOrderRow.id}`
      : "purchase"

    const response = await api(url, {
      method,
      body: JSON.stringify({ ...data, products: getValues("products") }),
    })

    if (response) {
      queryClient.invalidateQueries({ queryKey: ["purchase"] })
      toast.success(
        `Purchase successfully ${selectedOrderRow ? "updated" : "added"}`
      )
      onClose ? onClose() : defaultOnClose()
      reset()
    }
  }

  const [openSupplierModel, setOpenSupplierModel] = useState(false)
  const handleSupplierModelClose = useCallback(() => {
    setOpenSupplierModel(false)
    queryClient.invalidateQueries("suppliers")
  }, [queryClient])

  return (
    <>
      <Modal
        open={open ? open : openOrderDialog}
        onClose={defaultOnClose}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          overflow: "auto",
        }}
      >
        <Box
          sx={{
            margin: "100px auto",
            bgcolor: "white",
            width: "80vw",
            maxHeight: "90vh",
            overflowY: "auto",
            borderRadius: 1,
            boxShadow: 3,
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderRadius: 1,
              background: "#f6f7f9",
              padding: { xs: "15px 20px", md: "25px" },
            }}
          >
            <Typography
              id="modal-modal-title"
              variant="h6"
              sx={{
                fontWeight: "600",
                fontSize: { xs: "16px", md: "18px" },
              }}
              className="text-black"
            >
              {selectedOrderRow ? "Update Purchase " : "Add New Purchase "}
            </Typography>
            <IconButton
              aria-label="remove"
              size="small"
              onClick={onClose ? onClose : defaultOnClose}
            >
              <ClearIcon />
            </IconButton>
          </Box>

          <Box
            sx={{ padding: 2 }}
            component="form"
            onSubmit={handleSubmit(onSubmit)}
          >
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4, lg: 4 }}>
                <FormControl fullWidth>
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <SingleSelect
                        name="supplier_id"
                        controller={Controller}
                        control={control}
                        label="Supplier"
                        selectedValue={selectedOrderRow?.supplier_id ?? ""}
                        options={suppliers}
                        loading={supplierLoading}
                        error={!!errors.supplier_id}
                        helperText={errors.supplier_id?.message}
                        optionLabel="name"
                        optionValue="id"
                      />
                    </Box>
                    {canCreateSupplier && (
                      <Button
                        variant="contained"
                        onClick={() => setOpenSupplierModel(true)}
                        sx={{
                          minWidth: "48px",
                          width: "48px",
                          height: "55px",
                          padding: 0,
                          mt: "3px", // Align with the select input
                        }}
                      >
                        <AddIcon />
                      </Button>
                    )}
                  </Box>
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <Controller
                    name="date"
                    control={control}
                    rules={{
                      validate: (value) =>
                        value && dayjs(value, "YYYY-MM-DD", true).isValid()
                          ? true
                          : "Invalid date format. Use YYYY-MM-DD.",
                    }}
                    render={({
                      field: { onChange, value },
                      fieldState: { error },
                    }) => (
                      <DatePicker
                        label="Date"
                        value={value ? dayjs(value) : null} // Convert value to dayjs instance if it exists
                        onChange={(newValue) => {
                          onChange(
                            newValue
                              ? dayjs(newValue).format("YYYY-MM-DD")
                              : null
                          )
                        }}
                        sx={{
                          width: "100%",
                          "& fieldset": {
                            border: "1px solid #D5D9E2",
                            borderRadius: "7px",
                          },
                        }}
                        maxDate={dayjs()}
                        slotProps={{
                          textField: {
                            error: !!error,
                            helperText: error?.message,
                          },
                        }}
                      />
                    )}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid size={{ xs: 12, md: 4 }}>
                <div>
                  <TextField
                    fullWidth
                    label="Reference Number"
                    sx={{ marginBottom: 2 }}
                    error={!!errors.reference_number}
                    helperText={
                      errors.reference_number
                        ? errors.reference_number.message?.toString()
                        : ""
                    }
                    {...register("reference_number")}
                  />
                </div>
              </Grid>
            </Grid>

            {/* Productable Component */}
            <PurchaseProductable
              label="Product Items"
              name="products"
              controller={Controller}
              control={control}
              errors={errors}
              defaultProducts={selectedOrderRow?.products || []}
              helperText={errors.products?.message}
              onClose={onClose}
              isSubmitting={isSubmitting}
            />

            <Grid size={12} mt={1}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "end",
                  gap: "10px",
                }}
              >
                <SecondaryButton label="Cancel" onClick={defaultOnClose} />
                <PrimaryButton
                  type="submit"
                  label="Create"
                  isSubmitting={isSubmitting}
                />
              </Box>
            </Grid>

            <DevTool control={control} />
          </Box>
          {openSupplierModel ? (
            <AddSupplierModal
              open={openSupplierModel}
              onClose={handleSupplierModelClose}
            />
          ) : null}
        </Box>
      </Modal>
    </>
  )
}

export default AddPurchaseModal
